import { getLogger } from "@logtape/logtape";
import type { z } from "zod";
import type { Endpoint, FetchEndpointRequestDataSchema } from "../schemas";
import { parseOpenApiSpecToEndpoints } from "../utils/openapi";

const logger = getLogger(["services-api", "preview-service"]);

export type PreviewServiceError = {
  code: string;
  message: string;
  details?: unknown;
};

export type PreviewServiceResult<T> =
  | {
      success: true;
      data: T;
      headers?: Record<string, string>;
    }
  | {
      success: false;
      error: PreviewServiceError;
      headers?: Record<string, string>;
    };

export type FetchEndpointRequestData = z.infer<
  typeof FetchEndpointRequestDataSchema
>;

export type FetchEndpointResponse = {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: string;
  url: string;
  method: string;
  duration: number;
};

/**
 * Fetches and parses the OpenAPI specification from a deployed app
 * @param appName - The name of the deployed app
 * @returns Array of endpoint definitions or empty array if spec not available
 */
export async function listAppEndpoints(
  appName: string,
): Promise<PreviewServiceResult<Endpoint[]>> {
  try {
    // Fetch OpenAPI spec from the deployed app
    const openApiUrl = `https://${appName}.fp.dev/openapi.json`;
    const openApiResponse = await fetch(openApiUrl);

    if (!openApiResponse.ok) {
      if (openApiResponse.status === 404) {
        // 404 could mean either:
        // 1. App exists but no OpenAPI spec
        // 2. App doesn't exist (Cloudflare returns 404 for non-existent workers)
        // We need to check if the app itself exists by analyzing response characteristics
        try {
          const baseResponse = await fetch(`https://${appName}.fp.dev/`, {
            // Add timeout to prevent hanging requests
            signal: AbortSignal.timeout(10000),
          });

          const contentType = baseResponse.headers.get("content-type");
          const server = baseResponse.headers.get("server");

          // Read response body once and analyze it
          const responseText = await baseResponse.text();

          // Analyze response characteristics to determine if worker exists
          if (baseResponse.status === 404) {
            // Check for specific Cloudflare Worker indicators
            const isWorkerResponse =
              contentType?.includes("application/json") ||
              responseText.includes('"message"') ||
              responseText.includes('"error"') ||
              server?.toLowerCase().includes("cloudflare-workers");

            const isGenericCloudflareError =
              responseText.includes("Worker not found") ||
              responseText.includes("The requested URL was not found") ||
              contentType?.includes("text/html");

            if (isWorkerResponse && !isGenericCloudflareError) {
              // Worker exists and returned a structured 404 - app is deployed
              return {
                success: false,
                error: {
                  code: "OPENAPI_NOT_FOUND",
                  message:
                    "OpenAPI specification not found - app deployed but no OpenAPI support",
                  details: { status: 404, responseType: "worker_response" },
                },
              };
            }

            if (isGenericCloudflareError) {
              // Generic Cloudflare response - worker doesn't exist
              return {
                success: false,
                error: {
                  code: "APP_NOT_DEPLOYED",
                  message: "App not deployed",
                  details: { status: 404, responseType: "cloudflare_generic" },
                },
              };
            }

            // Ambiguous case - be conservative and assume not deployed
            return {
              success: false,
              error: {
                code: "APP_NOT_DEPLOYED",
                message: "App not deployed",
                details: { status: 404, responseType: "ambiguous" },
              },
            };
          }
          // Base URL returned non-404, app definitely exists
          return {
            success: false,
            error: {
              code: "OPENAPI_NOT_FOUND",
              message:
                "OpenAPI specification not found - app deployed but no OpenAPI support",
              details: { status: 404, baseUrlStatus: baseResponse.status },
            },
          };
        } catch (error) {
          // Handle different types of errors
          logger.error("Error checking app deployment status", {
            appName,
            error,
          });
          if (error instanceof Error) {
            if (
              error.name === "AbortError" ||
              error.message.includes("timeout")
            ) {
              // Timeout - likely means app doesn't exist or network issues
              return {
                success: false,
                error: {
                  code: "APP_NOT_DEPLOYED",
                  message: "App not deployed",
                  details: { status: 404, error: "timeout" },
                },
              };
            }

            if (error.message.includes("fetch")) {
              // Network error - app likely doesn't exist
              return {
                success: false,
                error: {
                  code: "APP_NOT_DEPLOYED",
                  message: "App not deployed",
                  details: { status: 404, error: "network_error" },
                },
              };
            }
          }

          // Unknown error - be conservative and assume not deployed
          return {
            success: false,
            error: {
              code: "APP_NOT_DEPLOYED",
              message: "App not deployed",
              details: { status: 404, error: "unknown" },
            },
          };
        }
      }
      // Other errors (5xx, etc.) - treat as deployment issue
      return {
        success: false,
        error: {
          code: "APP_ERROR",
          message: "App deployment error",
          details: { status: openApiResponse.status },
        },
      };
    }

    const openApiSpec = await openApiResponse.json();

    // Parse OpenAPI spec to extract endpoints
    const endpoints = parseOpenApiSpecToEndpoints(openApiSpec);

    return {
      success: true,
      data: endpoints,
    };
  } catch (error) {
    return {
      success: false,
      error: {
        code: "OPENAPI_FETCH_FAILED",
        message: "Failed to fetch OpenAPI specification",
        details: error,
      },
    };
  }
}

/**
 * Makes an HTTP request to a deployed app
 * @param appName - The name of the deployed app
 * @param requestData - The request configuration
 * @returns Response data with status, headers, body, and timing
 */
export async function fetchAppEndpoint(
  appName: string,
  requestData: FetchEndpointRequestData,
): Promise<PreviewServiceResult<FetchEndpointResponse>> {
  const startTime = Date.now();

  try {
    // Replace path parameters with actual values from params
    let processedPath = requestData.path;
    const usedParamNames = new Set<string>();

    if (requestData.params) {
      for (const param of requestData.params) {
        if (param.name && param.value) {
          const paramPattern = `{${param.name}}`;
          if (processedPath.includes(paramPattern)) {
            processedPath = processedPath.replace(paramPattern, param.value);
            usedParamNames.add(param.name);
          }
        }
      }
    }

    // Construct the full URL using the canonical app name and processed path
    const url = new URL(`https://${appName}.fp.dev${processedPath}`);

    // Add query parameters (excluding those used in path)
    if (requestData.params) {
      for (const param of requestData.params) {
        if (param.name && param.value && !usedParamNames.has(param.name)) {
          // Use append() to support repeated query parameters
          url.searchParams.append(param.name, param.value);
        }
      }
    }

    // Prepare headers
    const headers: Record<string, string> = {};
    if (requestData.headers) {
      for (const header of requestData.headers) {
        if (header.name && header.value) {
          headers[header.name] = header.value;
        }
      }
    }

    // Prepare fetch options
    const fetchOptions: RequestInit = {
      method: requestData.method,
      headers,
    };

    if (requestData.body && !["GET", "HEAD"].includes(requestData.method)) {
      fetchOptions.body = requestData.body;
    }

    // Make the request to the deployed app
    const response = await fetch(url.toString(), fetchOptions);
    const responseBody = await response.text();
    const endTime = Date.now();

    // Collect response headers
    const responseHeaders: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });

    return {
      success: true,
      data: {
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
        body: responseBody,
        url: url.toString(),
        method: requestData.method,
        duration: endTime - startTime,
      },
    };
  } catch (error) {
    let errorCode = "FETCH_ENDPOINT_FAILED";
    let errorMessage = "Failed to make fetch endpoint request";

    if (error instanceof TypeError && error.message.includes("fetch")) {
      errorCode = "INVALID_URL_OR_NETWORK_ERROR";
      errorMessage = "Invalid URL or network error";
    }

    return {
      success: false,
      error: {
        code: errorCode,
        message: errorMessage,
        details: error,
      },
    };
  }
}

/**
 * Calls the deployed app's MCP endpoint with a JSON-RPC request
 * @param appName - The name of the deployed app
 * @param requestBody - JSON-RPC 2.0 request body
 * @returns Parsed JSON from the MCP response or a descriptive error
 */
export async function callMcpRpc(
  appName: string,
  requestBody: unknown,
  options?: { sessionId?: string },
): Promise<PreviewServiceResult<unknown>> {
  // TODO remove -preview from the URL
  const url = `https://${appName}-preview.fp.dev/mcp`;
  return callMcpRpcToUrl(url, requestBody, options);
}

/**
 * Calls an arbitrary MCP endpoint URL with a JSON-RPC request (supports http/https)
 */
export async function callMcpRpcToUrl(
  url: string,
  requestBody: unknown,
  options?: { sessionId?: string },
): Promise<PreviewServiceResult<unknown>> {
  try {
    // Optional timeout if available
    let signal: AbortSignal | undefined;
    const AbortSignalWithTimeout = AbortSignal as unknown as {
      timeout?: (ms: number) => AbortSignal;
    };
    if (typeof AbortSignalWithTimeout.timeout === "function") {
      signal = AbortSignalWithTimeout.timeout(15000);
    }

    const headers = new Headers({
      "Content-Type": "application/json",
      // Some MCP servers require clients to accept both JSON and SSE.
      // Prefer JSON explicitly via quality values while still advertising SSE support.
      Accept: "application/json; q=1, text/event-stream; q=0.5",
      "User-Agent": "Fiberplane-Services-API/1.0",
      "X-Client-Name": "fiberplane-services",
      "X-Client-Version": "1.0.0",
      "X-Client-Platform": "cloudflare-worker",
    });

    if (options?.sessionId) {
      headers.set("mcp-session-id", options.sessionId);
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(requestBody),
      // Ensure the request doesn't hang indefinitely if server streams SSE endlessly
      signal,
    } as RequestInit);

    const sessionId = response.headers.get("mcp-session-id") || undefined;
    const buildHeaders = () =>
      sessionId ? { "mcp-session-id": sessionId } : undefined;

    if (response.status === 404) {
      return {
        success: false,
        error: {
          code: "MCP_NOT_FOUND",
          message: "MCP endpoint not found on deployed app",
          details: { status: 404 },
        },
        headers: buildHeaders(),
      };
    }

    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("text/event-stream")) {
      // Minimal SSE parser: return on first JSON message containing result or error
      const reader = response.body?.getReader();
      if (!reader) {
        return {
          success: false,
          error: {
            code: "MCP_SSE_UNSUPPORTED",
            message: "SSE response without readable body",
            details: { url },
          },
          headers: buildHeaders(),
        };
      }

      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split("\n\n");
        // keep last incomplete chunk in buffer
        buffer = parts.pop() || "";

        for (const part of parts) {
          const lines = part.split("\n");
          const dataLines = lines
            .filter((l) => l.startsWith("data:"))
            .map((l) => l.slice(5).trim())
            .join("\n");
          if (!dataLines) {
            continue;
          }
          try {
            const obj = JSON.parse(dataLines);
            if (obj && typeof obj === "object") {
              if (obj.result !== undefined) {
                return {
                  success: true,
                  data: obj,
                  headers: buildHeaders(),
                };
              }
              if (obj.error) {
                return {
                  success: false,
                  error: {
                    code: "MCP_ERROR",
                    message: "MCP endpoint returned an error",
                    details: { status: response.status, data: obj, url },
                  },
                  headers: buildHeaders(),
                };
              }
            }
          } catch {
            // ignore JSON parse errors; continue reading
          }
        }
      }

      // Stream ended without a conclusive message
      return {
        success: false,
        error: {
          code: "MCP_SSE_EOF",
          message: "SSE stream ended without result",
          details: { url },
        },
        headers: buildHeaders(),
      };
    }

    // Non-SSE: try JSON first, then text fallback
    const data = await response.json().catch(async () => {
      const text = await response.text();
      return { raw: text };
    });

    if (!response.ok) {
      return {
        success: false,
        error: {
          code: "MCP_ERROR",
          message: "MCP endpoint returned an error",
          details: { status: response.status, data, url },
        },
        headers: buildHeaders(),
      };
    }

    return { success: true, data, headers: buildHeaders() };
  } catch (error) {
    return {
      success: false,
      error: {
        code: "MCP_FETCH_FAILED",
        message: "Failed to call MCP endpoint",
        details: {
          error:
            error instanceof Error
              ? { message: error.message, stack: error.stack }
              : String(error),
          url,
        },
      },
    };
  }
}

export async function terminateMcpSession(
  url: string,
  sessionId: string,
): Promise<PreviewServiceResult<{ terminated: boolean }>> {
  try {
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "mcp-session-id": sessionId,
      },
    });

    if (response.status === 405) {
      return {
        success: true,
        data: {
          terminated: false,
        },
      };
    }

    if (!response.ok) {
      return {
        success: false,
        error: {
          code: "MCP_TERMINATE_FAILED",
          message: `Failed to terminate MCP session: ${response.statusText}`,
          details: { status: response.status, url },
        },
      };
    }

    return {
      success: true,
      data: {
        terminated: true,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: {
        code: "MCP_TERMINATE_ERROR",
        message: "Failed to terminate MCP session",
        details: {
          error:
            error instanceof Error
              ? { message: error.message, stack: error.stack }
              : String(error),
          url,
        },
      },
    };
  }
}
